import { ChangeDetectorRef, Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Call, CallAccessLevel, CallPublicLinkAvailability, CallsService } from '../../calls.service'
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogTitle } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete'
import { MatSelect } from '@angular/material/select'
import { filter, firstValueFrom, map } from 'rxjs'
import { ToastService } from '../../toast.service'
import { MatInput } from '@angular/material/input'
import { User, UsersService } from '../../users.service'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { MatChip } from '@angular/material/chips'
import { CdkCopyToClipboard } from '@angular/cdk/clipboard'
import { constructNameForUser } from '../../utils'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { UserNamePipe } from '../../user-name.pipe'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-share-call-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatCheckbox,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    TranslatePipe,
    TranslateDirective,
    MatDialogClose,
    MatDivider,
    FormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatInput,
    MatAutocompleteModule,
    MatIcon,
    MatTooltip,
    MatDialogModule,
    MatChip,
    CdkCopyToClipboard,
    SpinnerComponent,
    UserNamePipe,
    AsyncPipe,
  ],
  templateUrl: './share-call-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './share-call-dialog.component.scss',
})
export class ShareCallDialogComponent implements OnInit {
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  usersService = inject(UsersService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  changeDetectorRef = inject(ChangeDetectorRef)

  updateShareLevelMutation = this.callsService.updateCallAccessLevel(this.call.id)
  shareToUserMutation = this.callsService.shareToUser(this.call.id)
  removeShareMutation = this.callsService.removeShareMutation(this.call.id)
  publicLinkAccessMutation = this.callsService.publicLinkAccessLevelMutation()

  isSharePending$ = this.shareToUserMutation.result$.pipe(map((result) => result.isPending))

  canEdit = false
  showShareForm = false
  userToShareTo: User | null = null
  email = ''
  level: CallAccessLevel = 'VIEWER'
  notifyByEmail = true
  filteredOrganizationUsers: User[] = []
  organizationUsers: User[] = []

  constructor(@Inject(MAT_DIALOG_DATA) public call: Call) {}

  ngOnInit(): void {
    this.initializeOrganizationUsers()
    this.canEdit = this.call.accessLevel === 'FULL_ACCESS'
  }

  async updateShareLevel(sharedUserId: number, level: CallAccessLevel) {
    try {
      await this.updateShareLevelMutation.mutateAsync({ sharedUserId, level })
      this.resetShareForm()
      this.toastService.show('CALLS.SHARE_DIALOG.UPDATED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('CALLS.SHARE_DIALOG.UPDATE_ERROR')
    }
  }

  filterOrganizationUsers(input: string): void {
    this.filteredOrganizationUsers = this.organizationUsers
      .filter((user) => !this.call.users.map((callUser) => callUser.id).includes(user.id))
      .filter((user) => !this.call.shares.map((share) => share.sharedUserId).includes(user.id))
      .filter((user) => user.id !== this.usersService.currentUser$.value?.id)
      .filter((user) => (constructNameForUser(user) + user.email).includes(input))
  }

  displayUserOnAutocomplete(user: User): string {
    return user !== null && typeof user === 'object' ? constructNameForUser(user) : ''
  }

  async shareCall(): Promise<void> {
    const userToShareTo = this.userToShareTo ?? this.organizationUsers.find((user) => user.email === this.email) ?? null
    if (userToShareTo === null) {
      return
    }
    try {
      const share = await this.shareToUserMutation.mutateAsync({
        sharedUserId: userToShareTo.id,
        level: this.level,
        notifyByEmail: this.notifyByEmail,
      })
      this.call.shares.push(share)
      this.resetShareForm()
      this.toastService.show('CALLS.SHARE_DIALOG.SHARED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('CALLS.SHARE_DIALOG.SHARE_ERROR')
    }
  }

  async removeShare(sharedUser: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.TITLE'),
        description: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.DESCRIPTION', { userName: constructNameForUser(sharedUser) }),
      },
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      await this.removeShareMutation.mutateAsync(sharedUser.id)
      this.call.shares = this.call.shares.filter((share) => share.sharedUserId !== sharedUser.id)
      this.changeDetectorRef.detectChanges()
      this.toastService.show('CALLS.SHARE_DIALOG.SHARE_REMOVED_NOTIFICATION')
    }
  }

  async updatePublicShareLevel(level: CallAccessLevel) {
    await this.publicLinkAccessMutation.mutateAsync({ callId: this.call.id, level: level, availability: undefined })
    this.call.publicLinkAccessLevel = level
    this.toastService.show('CALLS.SHARE_DIALOG.PUBLIC_SHARE_UPDATED')
  }

  async updatePublicLinkAvailability(availability: CallPublicLinkAvailability) {
    await this.publicLinkAccessMutation.mutateAsync({ callId: this.call.id, level: undefined, availability: availability })
    this.call.publicLinkAvailability = availability
    this.toastService.show('CALLS.SHARE_DIALOG.PUBLIC_SHARE_UPDATED')
  }

  isValidInput() {
    const isUserSelected = this.userToShareTo !== null

    const isAlreadySharedToUserByUserId = this.userToShareTo !== null && this.call.shares.map((share) => share.sharedUser.id).includes(this.userToShareTo.id)
    const isAlreadySharedToUserByEmail = this.call.shares.map((share) => share.sharedUser.email).includes(this.email)
    const isAlreadySharedToUser = isAlreadySharedToUserByUserId || isAlreadySharedToUserByEmail

    return isUserSelected && !isAlreadySharedToUser
  }

  get emailMatchesExistingUser() {
    return this.organizationUsers.map((user) => user.email).includes(this.email)
  }

  private resetShareForm() {
    this.showShareForm = false
    this.userToShareTo = null
    this.email = ''
    this.level = 'VIEWER'
  }

  private initializeOrganizationUsers() {
    this.usersService
      .getOrganizationUsers()
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((users) => (this.organizationUsers = users!))
  }

  get publicLink() {
    return `${window.location.origin}${window.location.pathname}?secret=${this.call.publicLinkSecret}`
  }
}
